import { c as _c } from "react/compiler-runtime";
import AccordionComponent, { Entry } from '../accordion/accordion.component';
import { Attribute, AttributeGroup, AttributeGroupIcon } from './AttributeTypes';
import { ProductDatasheet, ProductInfoProductAssetType, ProductTestRatingAsset } from '@spa/components/ProductAssetTypes';
import { ReactElement, memo } from 'react';
import AssetLinkComponent from '@spa/components/asset-link/asset-link.component';
import { BAZAARVOICE_REVIEW_SECTION_ID } from '@spa/components/bazaarvoice/bazaarvoiceReviewSectionAnchor';
import BazaarvoiceReviewSectionComponent from '@spa/components/bazaarvoice/bazaarvoice-review-section.component';
import { DynamicProductTestRatingComponent } from '../product-test-rating/dynamic-product-test-rating.component';
import IcecatProductStoryComponent from '@spa/components/icecat-product-story/icecat-product-story.component';
import ImageComponent from '@spa/components/image-component/image.component';
import { ImageLoader } from 'next/image';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import { ProductAudioTrack } from '@spa/generated/graphql/graphql';
import ProductRatingsComponent from '@spa/components/product-ratings/product-ratings.component';
import SpecificationsTableComponent from './specifications-table/specifications-table.component';
import TrackListComponent from '@spa/components/track-list/track-list.component';
import { dailyDosesInfoAttributeId } from '@spa/lib/const';
import selectGeneralSettings from '@spa/lib/store/settings/selectors/selectGeneralSettings';
import selectLocale from '@spa/lib/store/settings/selectors/selectLocale';
import selectPdpSettings from '@spa/lib/store/settings/selectors/selectPdpSettings';
import styles from './product-specifications.component.module.scss';
import useImagesLoader from '@spa/lib/react/hooks/useImagesLoader';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
import { useSelector } from 'react-redux';
export interface Props {
  readonly id: string;
  readonly code: string | null;
  readonly ean: string | null;
  readonly descriptionHtml: string | null;
  readonly attributeGroups: ReadonlyArray<AttributeGroup>;
  readonly audioTracks: ReadonlyArray<ProductAudioTrack> | null;
  readonly biocideRegistrationNumbers?: ReadonlyArray<string> | null;
  readonly cxBiocide: boolean;
  readonly datasheets?: ReadonlyArray<ProductDatasheet> | null;
  readonly testRatingAsset?: ProductTestRatingAsset | null;
  readonly editorMode: boolean;
  readonly icecatIntegrationEnabled?: boolean;
  readonly averageRating?: number | null;
  readonly reviewCount?: number | null;
}
type AttributeGroupProcessor = (group: AttributeGroup, imagesLoader: ImageLoader, collapse?: boolean) => ReactElement | null;
export enum AttributeGroupType {
  ARTICLE_DETAILS = 'articleDetails',
  CARE_INSTRUCTIONS = 'waschanleitung',
  CERTIFICATES = 'zertifizierung',
  DAILY_DOSE = 'tagesdosisInfo',
  NUTRITIONAL_VALUES = 'naehrwerte',
  SAFETY_INSTRUCTIONS = 'warnhinweis',
  WARNINGS = 'gefahrenhinweis',
}
interface AttributeGroupIconsProps {
  readonly icons: ReadonlyArray<AttributeGroupIcon>;
  readonly imagesLoader: ImageLoader;
}
function AttributeGroupIcons(t0) {
  const $ = _c(7);
  const {
    icons,
    imagesLoader
  } = t0;
  let t1;
  if ($[0] !== icons || $[1] !== imagesLoader) {
    let t2;
    if ($[3] !== imagesLoader) {
      t2 = t3 => {
        const {
          alt,
          height,
          title,
          url,
          width
        } = t3;
        return <ImageComponent key={url} alt={alt ?? ""} className={styles["attribute-group__icon"]} height={height} src={url} title={title ?? ""} width={width} sizes="(min-width: 768px) 90px, 60px" loader={imagesLoader} />;
      };
      $[3] = imagesLoader;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = icons.map(t2);
    $[0] = icons;
    $[1] = imagesLoader;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <div className={styles["attribute-group__icons-container"]}>{t1}</div>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
const createCareInstructions = (attributeGroup: AttributeGroup, imagesLoader: ImageLoader): ReactElement | null => {
  const {
    id,
    icons
  } = attributeGroup;
  let content: ReactElement | null = null;
  if (icons) {
    content = <div id={id} className={styles['attribute-group']}>
        <AttributeGroupIcons icons={icons} imagesLoader={imagesLoader} />
      </div>;
  }
  return content;
};
const createCertificates = (attributeGroup: AttributeGroup, imagesLoader: ImageLoader): ReactElement | null => {
  const {
    id,
    icons
  } = attributeGroup;
  let content: ReactElement | null = null;
  if (icons) {
    content = <div id={id} className={styles['attribute-group']}>
        <div className={styles['attribute-group__icons-container']}>
          {icons.map(({
          alt,
          height,
          title,
          url,
          width
        }) => <a key={url} href={url}>
              <div className={styles['attribute-group__icon-image-wrapper']}>
                <ImageComponent alt={alt ?? ''} className={styles['attribute-group__icon']} height={height} src={url} title={title ?? ''} width={width} sizes="(min-width: 768px) 90px, 60px" loader={imagesLoader} />
              </div>
            </a>)}
        </div>
      </div>;
  }
  return content;
};
const createWarnings = (attributeGroup: AttributeGroup, imagesLoader: ImageLoader): ReactElement | null => {
  const {
    id,
    description,
    icons,
    signalword
  } = attributeGroup;
  let content: ReactElement | null = null;
  if (icons || description) {
    content = <div id={id} className={styles['attribute-group']}>
        <div className={styles['attribute-group__warning-wrapper']}>
          {icons && <div className={styles['attribute-group__icons-container']}>
              {icons.map(({
            alt,
            height,
            title,
            url,
            width
          }) => <ImageComponent alt={alt ?? ''} className={styles['attribute-group__icon']} height={height} key={url} src={url} title={title ?? ''} width={width} sizes="(min-width: 768px) 90px, 60px" loader={imagesLoader} />)}
            </div>}
          <div className={styles['attribute-group__warning-description-wrapper']}>
            {signalword && <span className={styles['attribute-group__warning-signalword']}>{signalword}</span>}
            {description && <span className={styles['attribute-group__warning-description']}>{description}</span>}
          </div>
        </div>
      </div>;
  }
  return content;
};
const createDescriptionOnlyAttributeGroupContent = (attributeGroup: AttributeGroup): ReactElement | null => {
  const {
    id,
    description
  } = attributeGroup;
  let content: ReactElement | null = null;
  if (description) {
    content = <div id={id} className={styles['attribute-group']}>
        <span>{description}</span>
      </div>;
  }
  return content;
};
const createContentTable = ({
  attributes,
  titles,
  name
}: AttributeGroup, _imagesLoader: ImageLoader, collapse: boolean = false): ReactElement => {
  return <SpecificationsTableComponent attributeContent={attributes} labelName={name} titles={titles} collapse={collapse} />;
};
function ProductSpecificationsComponent(t0) {
  const $ = _c(94);
  const {
    id,
    code,
    ean,
    cxBiocide,
    biocideRegistrationNumbers,
    descriptionHtml,
    audioTracks,
    attributeGroups,
    datasheets,
    testRatingAsset,
    editorMode,
    icecatIntegrationEnabled: t1,
    averageRating: t2,
    reviewCount: t3
  } = t0;
  const icecatIntegrationEnabled = t1 === undefined ? false : t1;
  const averageRating = t2 === undefined ? null : t2;
  const reviewCount = t3 === undefined ? null : t3;
  const {
    showProductTestEnabled,
    bazaarvoiceEnabled
  } = useSelector(selectGeneralSettings);
  const {
    pdpShowExternalContentEnabled
  } = useSelector(selectPdpSettings);
  const {
    formatMessage
  } = useNamespacedIntl("product-specifications");
  const imagesLoader = useImagesLoader();
  const locale = useSelector(selectLocale);
  let t4;
  if ($[0] !== attributeGroups) {
    t4 = attributeGroups.find(_temp)?.attributes.find(_temp2);
    $[0] = attributeGroups;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const dailyDosesInfoAttribute = t4;
  let t5;
  if ($[2] !== imagesLoader) {
    t5 = group_0 => createContentTable(group_0, imagesLoader, true);
    $[2] = imagesLoader;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== dailyDosesInfoAttribute) {
    t6 = (group_1, loader) => <>{createContentTable({
        ...group_1,
        attributes: group_1.attributes.filter(_temp3)
      }, loader)}{dailyDosesInfoAttribute && <p className={styles.specifications__footnote}>{dailyDosesInfoAttribute.name}</p>}</>;
    $[4] = dailyDosesInfoAttribute;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let accordionEntries;
  if ($[6] !== attributeGroups || $[7] !== audioTracks || $[8] !== averageRating || $[9] !== bazaarvoiceEnabled || $[10] !== biocideRegistrationNumbers || $[11] !== code || $[12] !== cxBiocide || $[13] !== datasheets || $[14] !== descriptionHtml || $[15] !== ean || $[16] !== formatMessage || $[17] !== icecatIntegrationEnabled || $[18] !== imagesLoader || $[19] !== locale || $[20] !== pdpShowExternalContentEnabled || $[21] !== reviewCount || $[22] !== showProductTestEnabled || $[23] !== t5 || $[24] !== t6 || $[25] !== testRatingAsset) {
    const attributeGroupProcessorMapping = {
      [AttributeGroupType.CARE_INSTRUCTIONS]: createCareInstructions,
      [AttributeGroupType.WARNINGS]: createWarnings,
      [AttributeGroupType.SAFETY_INSTRUCTIONS]: createDescriptionOnlyAttributeGroupContent,
      [AttributeGroupType.CERTIFICATES]: createCertificates,
      [AttributeGroupType.ARTICLE_DETAILS]: t5,
      [AttributeGroupType.NUTRITIONAL_VALUES]: createContentTable,
      [AttributeGroupType.DAILY_DOSE]: t6
    };
    const importantDocuments = datasheets?.filter(_temp4) ?? [];
    let t7;
    if ($[27] !== formatMessage) {
      t7 = document => {
        const fallbackLabel = formatMessage("documentFallbackLinkText");
        let label = fallbackLabel;
        bb51: switch (document.type) {
          case ProductInfoProductAssetType.PRODUCT_INSTRUCTION:
            {
              label = formatMessage("instructionLinkText");
              break bb51;
            }
          case ProductInfoProductAssetType.PRODUCT_PACKAGE_INSERT:
            {
              label = formatMessage("packageInsertLinkText");
              break bb51;
            }
          case ProductInfoProductAssetType.PRODUCT_READING_SAMPLE:
            {
              label = formatMessage("readingSampleLinkText");
              break bb51;
            }
          case ProductInfoProductAssetType.PRODUCT_DECLARATION_OF_CONFORMITY:
            {
              label = formatMessage("declarationOfConformityLinkText", undefined, fallbackLabel);
              break bb51;
            }
          case ProductInfoProductAssetType.PRODUCT_DETAIL_DATASHEET:
            {
              label = formatMessage("datasheetLinkText");
              break bb51;
            }
          case ProductInfoProductAssetType.PRODUCT_LABORATORY_ANALYSIS:
            {
              label = formatMessage("laboratoryAnalysisLinkText", undefined, fallbackLabel);
              break bb51;
            }
          case ProductInfoProductAssetType.PRODUCT_SAFETY_DATA_SHEET:
            {
              label = formatMessage("safetyDataSheetLinkText");
            }
        }
        return document.contentType === "application/pdf" ? `${label} (PDF)` : label;
      };
      $[27] = formatMessage;
      $[28] = t7;
    } else {
      t7 = $[28];
    }
    const getImportantDocumentLabel = t7;
    accordionEntries = [];
    if (descriptionHtml) {
      let t8;
      if ($[29] !== descriptionHtml) {
        t8 = <div dangerouslySetInnerHTML={{
          __html: descriptionHtml
        }} />;
        $[29] = descriptionHtml;
        $[30] = t8;
      } else {
        t8 = $[30];
      }
      let t9;
      if ($[31] !== code || $[32] !== ean || $[33] !== icecatIntegrationEnabled || $[34] !== locale || $[35] !== pdpShowExternalContentEnabled) {
        t9 = pdpShowExternalContentEnabled && (icecatIntegrationEnabled ? <IcecatProductStoryComponent ean={ean} locale={locale} code={code} /> : <syndigo-powerpage pageid={code} />);
        $[31] = code;
        $[32] = ean;
        $[33] = icecatIntegrationEnabled;
        $[34] = locale;
        $[35] = pdpShowExternalContentEnabled;
        $[36] = t9;
      } else {
        t9 = $[36];
      }
      let t10;
      if ($[37] !== t8 || $[38] !== t9) {
        t10 = <>{t8}{t9}</>;
        $[37] = t8;
        $[38] = t9;
        $[39] = t10;
      } else {
        t10 = $[39];
      }
      accordionEntries.push({
        title: formatMessage("specifications.description"),
        content: t10,
        isInitialOpened: true,
        metadata: [],
        dateTo: null,
        dateFrom: null
      });
    }
    if (audioTracks) {
      let t8;
      if ($[40] !== audioTracks) {
        t8 = <TrackListComponent tracks={audioTracks} />;
        $[40] = audioTracks;
        $[41] = t8;
      } else {
        t8 = $[41];
      }
      accordionEntries.push({
        title: formatMessage("specifications.audioTracks"),
        content: t8,
        isInitialOpened: false,
        metadata: [],
        dateTo: null,
        dateFrom: null
      });
    }
    if (showProductTestEnabled && testRatingAsset) {
      const t8 = testRatingAsset.description ?? testRatingAsset.name ?? "";
      let t9;
      if ($[42] !== t8 || $[43] !== testRatingAsset.source) {
        t9 = <DynamicProductTestRatingComponent variant="standalone" source={testRatingAsset.source} alt={t8} />;
        $[42] = t8;
        $[43] = testRatingAsset.source;
        $[44] = t9;
      } else {
        t9 = $[44];
      }
      accordionEntries.push({
        title: formatMessage("specifications.testResult"),
        content: t9,
        isInitialOpened: false,
        metadata: [],
        dateTo: null,
        dateFrom: null
      });
    }
    let t8;
    if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = [AttributeGroupType.SAFETY_INSTRUCTIONS, AttributeGroupType.WARNINGS];
      $[45] = t8;
    } else {
      t8 = $[45];
    }
    const initialExpanded = t8;
    attributeGroups.forEach(group_2 => {
      const contentProcessor = attributeGroupProcessorMapping[group_2.id];
      const content = contentProcessor ? contentProcessor(group_2, imagesLoader) : null;
      if (content) {
        accordionEntries.push({
          title: formatMessage(`specifications.attributeGroups.${group_2.id}`),
          content,
          isInitialOpened: initialExpanded.includes(group_2.id as AttributeGroupType),
          metadata: [],
          dateTo: null,
          dateFrom: null
        });
      }
    });
    let t9;
    if ($[46] !== biocideRegistrationNumbers) {
      const t10 = biocideRegistrationNumbers ?? [];
      let t11;
      if ($[48] !== t10) {
        t11 = new Set(t10);
        $[48] = t10;
        $[49] = t11;
      } else {
        t11 = $[49];
      }
      t9 = Array.from(t11);
      $[46] = biocideRegistrationNumbers;
      $[47] = t9;
    } else {
      t9 = $[47];
    }
    const dedupedBiocideNumbers = t9;
    if (cxBiocide || dedupedBiocideNumbers.length > 0) {
      let t10;
      if ($[50] !== dedupedBiocideNumbers || $[51] !== formatMessage) {
        t10 = dedupedBiocideNumbers.length > 0 ? formatMessage("specifications.attributeGroups.biocideNumbers", {
          numbers: dedupedBiocideNumbers.join(", ")
        }) : null;
        $[50] = dedupedBiocideNumbers;
        $[51] = formatMessage;
        $[52] = t10;
      } else {
        t10 = $[52];
      }
      const biocideNumbersText = t10;
      let t11;
      if ($[53] !== formatMessage) {
        t11 = formatMessage("specifications.attributeGroups.biozidhinweis");
        $[53] = formatMessage;
        $[54] = t11;
      } else {
        t11 = $[54];
      }
      let t12;
      if ($[55] !== formatMessage) {
        t12 = formatMessage("specifications.attributeGroups.biocideNoticeDescription");
        $[55] = formatMessage;
        $[56] = t12;
      } else {
        t12 = $[56];
      }
      const t13 = biocideNumbersText && ` ${biocideNumbersText}`;
      let t14;
      if ($[57] !== t12 || $[58] !== t13) {
        t14 = <div className={styles["attribute-group"]}><span>{t12}{t13}</span></div>;
        $[57] = t12;
        $[58] = t13;
        $[59] = t14;
      } else {
        t14 = $[59];
      }
      let t15;
      if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
        t15 = [];
        $[60] = t15;
      } else {
        t15 = $[60];
      }
      let t16;
      if ($[61] !== t11 || $[62] !== t14) {
        t16 = {
          title: t11,
          content: t14,
          isInitialOpened: true,
          metadata: t15,
          dateTo: null,
          dateFrom: null
        };
        $[61] = t11;
        $[62] = t14;
        $[63] = t16;
      } else {
        t16 = $[63];
      }
      accordionEntries.push(t16);
    }
    if (importantDocuments.length > 0) {
      let t10;
      if ($[64] !== formatMessage) {
        t10 = formatMessage("importantInformation");
        $[64] = formatMessage;
        $[65] = t10;
      } else {
        t10 = $[65];
      }
      let t11;
      if ($[66] !== formatMessage || $[67] !== getImportantDocumentLabel) {
        t11 = document_0 => <AssetLinkComponent key={document_0.id} asset={document_0} fallbackTitle={formatMessage("datasheetLinkFallbackTitle")} showIcon={true}><span className={styles.specifications__datasheet}>{getImportantDocumentLabel(document_0)}</span></AssetLinkComponent>;
        $[66] = formatMessage;
        $[67] = getImportantDocumentLabel;
        $[68] = t11;
      } else {
        t11 = $[68];
      }
      const t12 = <div className={styles["specifications__documents-list"]}>{importantDocuments.map(t11)}</div>;
      let t13;
      if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
        t13 = [];
        $[69] = t13;
      } else {
        t13 = $[69];
      }
      let t14;
      if ($[70] !== t10 || $[71] !== t12) {
        t14 = {
          title: t10,
          content: t12,
          isInitialOpened: true,
          metadata: t13,
          dateTo: null,
          dateFrom: null
        };
        $[70] = t10;
        $[71] = t12;
        $[72] = t14;
      } else {
        t14 = $[72];
      }
      accordionEntries.push(t14);
    }
    const showBazaarVoiceRating = bazaarvoiceEnabled && (reviewCount ?? 0) > 0;
    if (bazaarvoiceEnabled && code) {
      let t10;
      if ($[73] !== formatMessage) {
        t10 = formatMessage("specifications.reviews");
        $[73] = formatMessage;
        $[74] = t10;
      } else {
        t10 = $[74];
      }
      let t11;
      if ($[75] !== code) {
        t11 = <BazaarvoiceReviewSectionComponent productCode={code} />;
        $[75] = code;
        $[76] = t11;
      } else {
        t11 = $[76];
      }
      let t12;
      if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
        t12 = [];
        $[77] = t12;
      } else {
        t12 = $[77];
      }
      let t13;
      if ($[78] !== averageRating || $[79] !== reviewCount || $[80] !== showBazaarVoiceRating) {
        t13 = showBazaarVoiceRating ? <ProductRatingsComponent averageRating={averageRating} reviewCount={reviewCount} enableReviewNavigation={false} /> : null;
        $[78] = averageRating;
        $[79] = reviewCount;
        $[80] = showBazaarVoiceRating;
        $[81] = t13;
      } else {
        t13 = $[81];
      }
      let t14;
      if ($[82] !== t10 || $[83] !== t11 || $[84] !== t13) {
        t14 = {
          title: t10,
          content: t11,
          isInitialOpened: false,
          allowContentOverflow: true,
          anchorId: BAZAARVOICE_REVIEW_SECTION_ID,
          metadata: t12,
          dateTo: null,
          dateFrom: null,
          headerAccessory: t13
        };
        $[82] = t10;
        $[83] = t11;
        $[84] = t13;
        $[85] = t14;
      } else {
        t14 = $[85];
      }
      const reviewsEntry = t14;
      accordionEntries.push(reviewsEntry);
    }
    $[6] = attributeGroups;
    $[7] = audioTracks;
    $[8] = averageRating;
    $[9] = bazaarvoiceEnabled;
    $[10] = biocideRegistrationNumbers;
    $[11] = code;
    $[12] = cxBiocide;
    $[13] = datasheets;
    $[14] = descriptionHtml;
    $[15] = ean;
    $[16] = formatMessage;
    $[17] = icecatIntegrationEnabled;
    $[18] = imagesLoader;
    $[19] = locale;
    $[20] = pdpShowExternalContentEnabled;
    $[21] = reviewCount;
    $[22] = showProductTestEnabled;
    $[23] = t5;
    $[24] = t6;
    $[25] = testRatingAsset;
    $[26] = accordionEntries;
  } else {
    accordionEntries = $[26];
  }
  const t7 = `${id}-accordion`;
  let t8;
  if ($[86] !== formatMessage) {
    t8 = formatMessage("headline");
    $[86] = formatMessage;
    $[87] = t8;
  } else {
    t8 = $[87];
  }
  let t9;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = [];
    $[88] = t9;
  } else {
    t9 = $[88];
  }
  let t10;
  if ($[89] !== accordionEntries || $[90] !== editorMode || $[91] !== t7 || $[92] !== t8) {
    t10 = <div className={styles.specifications}><AccordionComponent id={t7} headline={t8} entries={accordionEntries} entriesMetadata={t9} editorMode={editorMode} dateTo={null} dateFrom={null} /></div>;
    $[89] = accordionEntries;
    $[90] = editorMode;
    $[91] = t7;
    $[92] = t8;
    $[93] = t10;
  } else {
    t10 = $[93];
  }
  return t10;
}
function _temp4(t0) {
  const {
    type
  } = t0;
  return [ProductInfoProductAssetType.PRODUCT_DETAIL_DATASHEET, ProductInfoProductAssetType.PRODUCT_DECLARATION_OF_CONFORMITY, ProductInfoProductAssetType.PRODUCT_INSTRUCTION, ProductInfoProductAssetType.PRODUCT_LABORATORY_ANALYSIS, ProductInfoProductAssetType.PRODUCT_PACKAGE_INSERT, ProductInfoProductAssetType.PRODUCT_READING_SAMPLE, ProductInfoProductAssetType.PRODUCT_SAFETY_DATA_SHEET].includes(type);
}
function _temp3(attr_0) {
  return attr_0.id !== AttributeGroupType.DAILY_DOSE;
}
function _temp2(attr) {
  return attr.id === dailyDosesInfoAttributeId;
}
function _temp(group) {
  return group.id === AttributeGroupType.DAILY_DOSE;
}
export default memo(ProductSpecificationsComponent);